import { isWithinInterval, startOfDay, endOfDay } from 'date-fns'
import { z } from 'zod'

import i18n from '@/i18n'
import { formatDate } from '@/utils/format-date'
import { absenceReasonUnion, labelAndValue } from '@/utils/zod'

export const createFormSchema = (
  isSemesterArchived: boolean,
  semester?: {
    startDate: Date
    endDate: Date
  }
) =>
  z
    .object({
      student: labelAndValue(
        z.string(),
        i18n.t('error.student-is-required', { ns: 'absences2' })
      ),
      date: z
        .object({
          from: z.date().optional(),
          to: z.date().optional()
        })
        .refine(() => !isSemesterArchived, {
          message: i18n.t('error.semester-archived', { ns: 'absences2' })
        })
        .refine(data => data.from && data.to, {
          message: i18n.t('error.date-is-required', { ns: 'absences2' })
        })
        .refine(
          data => {
            if (!semester || !data.from || !data.to) return true

            return (
              isWithinInterval(startOfDay(data.from), {
                start: startOfDay(semester.startDate),
                end: endOfDay(semester.endDate)
              }) &&
              isWithinInterval(endOfDay(data.to), {
                start: startOfDay(semester.startDate),
                end: endOfDay(semester.endDate)
              })
            )
          },
          {
            message: i18n.t('error.date-must-be-within-semester', {
              ns: 'absences2',
              startDate: formatDate(semester?.startDate),
              endDate: formatDate(semester?.endDate)
            })
          }
        ),
      reason: absenceReasonUnion.optional(),
      sendForGuardianReview: z.boolean().optional(),
      comment: z.string().optional(),
      attachments: z.array(z.string()),
      lessons: z.array(z.string()).min(1, {
        message: i18n.t('error.select-at-least-one-lesson', { ns: 'absences2' })
      })
    })
    .superRefine((data, ctx) => {
      if (data.student && data.date.from && data.date.to && !data.reason) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('error.absence-reason-is-required', {
            ns: 'absences2'
          }),
          path: ['reason']
        })
      }
    })

export type AbsencesPlanFormPayload = z.infer<
  ReturnType<typeof createFormSchema>
>
